import { useState, useEffect, useRef } from 'react';
import { EventSummary } from '@/components/EventSummary';
import { useAuth } from '@/context/AuthContext';
import { eventsApi, schoolsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, XCircle, AlertTriangle, Filter, CheckCheck, Brain, Activity, Zap, SprayCan } from 'lucide-react';
import { format } from 'date-fns';
import { cn, utcDate } from '@/lib/utils';
import { Link, useSearchParams } from 'react-router-dom';
import { SchoolSelector } from '@/components/SchoolSelector';

const statusColors = {
  open: 'bg-red-100 text-red-800 border-red-200',
  acknowledged: 'bg-amber-100 text-amber-800 border-amber-200',
  false_positive: 'bg-slate-100 text-slate-600 border-slate-200',
  resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const statusLabels = {
  open: 'Öppen',
  acknowledged: 'Bekräftad',
  false_positive: 'Falskt positiv',
  resolved: 'Löst',
};

const severityConfig = {
  high: { label: 'HÖG', color: 'bg-red-600 text-white', border: 'border-red-400' },
  medium: { label: 'MEDEL', color: 'bg-orange-500 text-white', border: 'border-orange-400' },
  low: { label: 'LÅG', color: 'bg-slate-400 text-white', border: 'border-slate-300' },
};

const methodLabels = {
  ml: 'ML-detektion',
  threshold: 'Tröskelvärde',
  consecutive: 'Konsekutiv',
  spike: 'Spike-detektion',
  test: 'Testlarm',
};

function SeverityBadge({ severity }) {
  const config = severityConfig[severity] || severityConfig.low;
  return (
    <span
      className={cn("text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded", config.color)}
      data-testid={`severity-badge-${severity}`}
    >
      {config.label}
    </span>
  );
}

function DetectionMethodBadge({ method }) {
  if (!method) return null;
  const label = methodLabels[method] || method;
  const isML = method === 'ml';
  return (
    <span
      className={cn(
        "text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded inline-flex items-center gap-1",
        isML ? "bg-violet-100 text-violet-700 border border-violet-200" : "bg-slate-100 text-slate-600 border border-slate-200"
      )}
      data-testid={`method-badge-${method}`}
    >
      {isML ? <Brain className="h-2.5 w-2.5" /> : <Activity className="h-2.5 w-2.5" />}
      {label}
    </span>
  );
}

export default function EventsPage() {
  const { isSuperAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [typeFilter, schoolFilter, statusFilter, severityFilter]);

  const loadData = async () => {
    try {
      const params = {};
      if (typeFilter !== 'all') params.event_type = typeFilter;
      if (schoolFilter !== 'all') params.school_id = schoolFilter;
      if (statusFilter !== 'all') {
        if (statusFilter === 'open') params.acknowledged = false;
        else if (statusFilter === 'acknowledged') params.acknowledged = true;
      }
      
      const [eventsRes, schoolsRes] = await Promise.all([
        eventsApi.list(params),
        schoolsApi.list(),
      ]);
      let filtered = eventsRes.data;
      if (severityFilter !== 'all') {
        filtered = filtered.filter(e => e.severity === severityFilter);
      }
      setEvents(filtered);
      setSchools(schoolsRes.data);
    } catch (error) {
      toast.error('Kunde inte ladda händelser');
    } finally {
      setLoading(false);
    }
  };

  // Auto-scroll to highlighted event from push notification
  useEffect(() => {
    if (highlightId && !loading && events.length > 0) {
      setTimeout(() => {
        const el = document.querySelector(`[data-event-id="${highlightId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2'), 4000);
        }
        // Clear highlight param after scrolling
        setSearchParams({}, { replace: true });
      }, 300);
    }
  }, [highlightId, loading, events]);

  const handleAcknowledge = async (eventId) => {
    try {
      await eventsApi.acknowledge(eventId, { acknowledged_by: 'admin' });
      toast.success('Händelse bekräftad');
      loadData();
    } catch (error) {
      toast.error('Kunde inte bekräfta händelsen');
    }
  };

  const handleFalsePositive = async (eventId) => {
    try {
      await eventsApi.markFalsePositive(eventId, { false_positive_by: 'admin' });
      toast.success('Markerad som falskt positiv');
      loadData();
    } catch (error) {
      toast.error('Kunde inte markera som falskt positiv');
    }
  };

  const handleMarkSpray = async (eventId) => {
    try {
      await eventsApi.markSpray(eventId);
      toast.success('Markerad som spray — din feedback förbättrar detekteringen!', { duration: 4000 });
      loadData();
    } catch (error) {
      toast.error('Kunde inte markera som spray');
    }
  };

  const handleConfirmVape = async (eventId) => {
    try {
      await eventsApi.confirmVape(eventId);
      toast.success('Vape bekräftad — din feedback förbättrar detekteringen!', { duration: 4000 });
      loadData();
    } catch (error) {
      toast.error('Kunde inte bekräfta vape');
    }
  };

  const handleResolve = async (eventId) => {
    try {
      await eventsApi.resolve(eventId, { resolved_by: 'admin' });
      toast.success('Händelse löst');
      loadData();
    } catch (error) {
      toast.error('Kunde inte lösa händelsen');
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar händelser...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="events-page">
      <div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">
          Händelser
        </h1>
        <p className="text-slate-500 mt-1 font-ibm">
          Detektionshändelser och larm från alla enheter
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[150px] bg-white" data-testid="type-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Handelsetyp" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla typer</SelectItem>
            <SelectItem value="vape">Vape</SelectItem>
            <SelectItem value="suspect">Misstänkt</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="test_alarm">Testlarm</SelectItem>
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="severity-filter">
            <Zap className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Allvarlighetsgrad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla nivåer</SelectItem>
            <SelectItem value="high">Hög</SelectItem>
            <SelectItem value="medium">Medel</SelectItem>
            <SelectItem value="low">Låg</SelectItem>
          </SelectContent>
        </Select>

        <SchoolSelector schools={schools} value={schoolFilter} onChange={setSchoolFilter} triggerClassName="w-[180px] bg-white" />

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="status-filter">
            <SelectValue placeholder="Handelsestatus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla statusar</SelectItem>
            <SelectItem value="open">Öppen</SelectItem>
            <SelectItem value="acknowledged">Bekräftad</SelectItem>
            <SelectItem value="resolved">Löst</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {events.map((event) => (
          <Card
            key={event.id}
            className={cn(
              "border",
              event.severity === 'high' && "border-red-300 bg-red-50/80",
              event.severity === 'medium' && "border-orange-200 bg-orange-50/60",
              !event.severity && event.event_type === 'vape' && "border-red-200 bg-red-50",
              !event.severity && event.event_type === 'suspect' && "border-amber-200 bg-amber-50",
              event.event_type === 'offline' && "border-slate-200 bg-slate-50",
              event.event_type === 'test_alarm' && "border-purple-200 bg-purple-50",
              event.event_type === 'normal' && "border-emerald-200 bg-emerald-50"
            )}
            data-testid={`event-card-${event.id}`}
            data-event-id={event.id}
          >
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {event.is_test && (
                      <span className="text-xs font-ibm font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-blue-200 text-blue-800">
                        TEST
                      </span>
                    )}
                    <span className={cn(
                      "text-xs font-ibm font-bold uppercase tracking-wide px-2 py-0.5 rounded",
                      event.event_type === 'vape' && "bg-red-200 text-red-800",
                      event.event_type === 'suspect' && "bg-amber-200 text-amber-800",
                      event.event_type === 'offline' && "bg-slate-200 text-slate-700",
                      event.event_type === 'test_alarm' && "bg-purple-200 text-purple-800",
                      event.event_type === 'normal' && "bg-emerald-200 text-emerald-800"
                    )} data-testid={`event-type-badge-${event.id}`}>
                      {event.event_type === 'vape' ? 'VAPE' :
                       event.event_type === 'suspect' ? 'MISSTÄNKT' :
                       event.event_type === 'offline' ? 'OFFLINE' :
                       event.event_type === 'test_alarm' ? 'TESTLARM' :
                       event.event_type === 'normal' ? 'NORMAL' :
                       event.event_type.replace('_', ' ')}
                    </span>
                    {event.severity && <SeverityBadge severity={event.severity} />}
                    <span className={cn(
                      "text-xs font-ibm font-semibold uppercase px-2 py-0.5 rounded border",
                      statusColors[event.status] || statusColors.open
                    )} data-testid={`event-status-badge-${event.id}`}>
                      {statusLabels[event.status] || statusLabels.open}
                    </span>
                    {event.detection_method && <DetectionMethodBadge method={event.detection_method} />}
                    <span className="text-xs text-slate-500 tabular-nums">
                      {format(utcDate(event.created_at), 'd MMM yyyy HH:mm:ss')}
                    </span>
                  </div>
                  
                  <Link 
                    to={`/devices/${event.device_id}`}
                    className="font-ibm font-semibold text-slate-900 hover:text-navy"
                  >
                    {event.device_name}
                  </Link>
                  <p className="text-sm text-slate-600">
                    {event.school_name} - {event.room_name}
                  </p>
                  <EventSummary event={event} className="mt-2" />
                  {isSuperAdmin() && event.voc_fingerprint && event.voc_fingerprint.substance_type !== 'unknown' && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs font-ibm font-semibold px-2 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200" data-testid={`voc-fingerprint-${event.id}`}>
                        {event.voc_fingerprint.description}
                      </span>
                      <span className="text-xs text-slate-400">
                        ({Math.round(event.voc_fingerprint.confidence * 100)}% säkerhet)
                      </span>
                    </div>
                  )}
                  {isSuperAdmin() && event.time_multiplier && event.time_multiplier !== 1.0 && (
                    <span className="text-xs text-slate-400 mt-1 block" data-testid={`time-multiplier-${event.id}`}>
                      Tidsmönster: {event.time_multiplier}x
                    </span>
                  )}
                  {event.resolution_notes?.includes('Spray') && (
                    <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1 font-ibm font-semibold flex items-center gap-1" data-testid={`spray-label-${event.id}`}>
                      <SprayCan className="h-3 w-3" />
                      Spray (ej vape) — markerad av personal
                    </p>
                  )}
                </div>
                
                {(event.status === 'open' || !event.status) && event.event_type !== 'normal' && (
                  <div className="flex flex-row flex-wrap sm:flex-col gap-2 sm:ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAcknowledge(event.id)}
                      className="text-xs"
                      data-testid={`acknowledge-btn-${event.id}`}
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Kvittera
                    </Button>
                    {(event.event_type === 'vape' || event.event_type === 'suspect') && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleConfirmVape(event.id)}
                          className="text-xs text-green-700 border-green-200 hover:bg-green-50 dark:text-green-400 dark:border-green-800 dark:hover:bg-green-900/30"
                          data-testid={`confirm-vape-btn-${event.id}`}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Bekräfta vape
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkSpray(event.id)}
                          className="text-xs text-cyan-700 border-cyan-200 hover:bg-cyan-50 dark:text-cyan-400 dark:border-cyan-800 dark:hover:bg-cyan-900/30"
                          data-testid={`mark-spray-btn-${event.id}`}
                        >
                          <SprayCan className="h-3 w-3 mr-1" />
                          Spray (ej vape)
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleFalsePositive(event.id)}
                      className="text-xs text-slate-500"
                      data-testid={`false-positive-btn-${event.id}`}
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      Falskt +
                    </Button>
                  </div>
                )}
                
                {event.status === 'acknowledged' && (
                  <div className="flex flex-col gap-2 ml-4">
                    {(event.event_type === 'vape' || event.event_type === 'suspect') && (
                      <>
                        <p className="text-[10px] text-amber-600 dark:text-amber-400 font-ibm font-semibold" data-testid={`classify-nudge-${event.id}`}>
                          Klassificera för bättre detektion:
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleConfirmVape(event.id)}
                          className="text-xs text-green-700 border-green-200 hover:bg-green-50 dark:text-green-400"
                          data-testid={`ack-confirm-vape-${event.id}`}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Bekräfta vape
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkSpray(event.id)}
                          className="text-xs text-cyan-700 border-cyan-200 hover:bg-cyan-50 dark:text-cyan-400"
                          data-testid={`ack-mark-spray-${event.id}`}
                        >
                          <SprayCan className="h-3 w-3 mr-1" />
                          Spray
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResolve(event.id)}
                      className="text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                      data-testid={`resolve-btn-${event.id}`}
                    >
                      <CheckCheck className="h-3 w-3 mr-1" />
                      Lös
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {events.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-chivo text-lg text-slate-700">Inga händelser hittades</h3>
              <p className="text-slate-500 mt-1 font-ibm">
                Justera dina filter eller vänta på enhetshändelser
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
